import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, firstValueFrom, map, of, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { User } from '../models/user';
import jwt_decode from "jwt-decode";
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from './message.service';

/**
 * How the owner of an e-mail signs in, as the backend answers it: with a password, or through the
 * directory of their organisation. `domain` is what the start route takes, never the e-mail.
 */
export type SsoDiscovery =
  | { mode: 'password' }
  | { mode: 'sso'; profile: string; label: string; passwordAllowed: boolean; domain: string };

/** What the sign-in screens keep of an SSO answer to draw the directory button. */
export interface SsoButton {
  label: string;
  domain: string;
  passwordAllowed: boolean;
  logo?: string;
  /** Material icon shown instead of a logo. */
  icon?: string;
  /** Translation key of the button text. */
  text: string;
}

/**
 * The official mark of each directory, only where its owner publishes button rules that allow it (Google,
 * Microsoft). Okta publishes none and its content terms restrict the use of its mark: like generic OIDC,
 * its button carries a neutral key, the usual sign of a work sign-in, next to the directory's name.
 */
const DIRECTORY_LOGOS: Record<string, string> = {
  google: '/assets/img/directories/google.svg',
  'entra-tenant': '/assets/img/directories/microsoft.svg',
  'entra-multitenant': '/assets/img/directories/microsoft.svg',
};
const DIRECTORY_ICONS: Record<string, string> = {
  okta: 'key',
  oidc: 'key',
};
/** Microsoft's branding guidelines ask for « Sign in with Microsoft » next to its logo; « Continuer avec » otherwise. */
const DIRECTORY_TEXTS: Record<string, string> = {
  'entra-tenant': 'LOGIN.SIGN_IN_WITH_MICROSOFT',
  'entra-multitenant': 'LOGIN.SIGN_IN_WITH_MICROSOFT',
};

export function ssoButton(found: Extract<SsoDiscovery, { mode: 'sso' }>): SsoButton {
  return {
    label: found.label,
    domain: found.domain,
    passwordAllowed: found.passwordAllowed,
    logo: DIRECTORY_LOGOS[found.profile],
    icon: DIRECTORY_ICONS[found.profile],
    text: DIRECTORY_TEXTS[found.profile] ?? 'LOGIN.CONTINUE_WITH',
  };
}

export interface Session {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface TokenDecoded {
  sub: string;
  email: string;
  role: {
    name: string;
    level: number;
  },
  organization: {
    id: string;
    logo: string;
    name: string;
  },
  iat: number;
  exp: number;
  iss: string;
}


@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {


  private userSubject: BehaviorSubject<User | undefined> = new BehaviorSubject<User | undefined>(undefined);
  public user: Observable<User | undefined>;

  constructor(
    private _message: MessageService,
    private http: HttpClient
  ) {
    const user = localStorage.getItem('uzai-user');
    this.userSubject.next(user ? JSON.parse(user) : undefined);
    this.user = this.userSubject.asObservable();

    // The session is shared by every tab: when another one enters, leaves or changes organisation (or signs
    // out), this tab would keep the old organisation in memory while its requests carry the new tokens.
    const started = this.userSubject.value;
    const sessionKey = (u?: User) => `${u?.organizationId ?? u?.organization ?? ''}|${u?.access?.id ?? ''}`;
    window.addEventListener('storage', (event) => {
      if (event.key !== 'uzai-user' && event.key !== null) return;
      const current = localStorage.getItem('uzai-user');
      if (sessionKey(current ? JSON.parse(current) : undefined) !== sessionKey(started)) {
        window.location.reload();
      }
    });
  }

  /** The user of the session as last stored, or undefined when signed out. */
  currentUser(): User | undefined {
    return this.userSubject.value;
  }

  getAccessToken() {
    return localStorage.getItem('uzai-access-token');
  }

  isAccessTokenAboutToExpire() {
    const accessToken = localStorage.getItem('uzai-access-token');
    if (accessToken) {
      const decoded = jwt_decode<TokenDecoded>(accessToken);
      const expiration = moment.unix(decoded.exp);
      return expiration.isSameOrBefore(moment().add(2, 'minutes'));
    }
    return false;
  }

  getRefreshToken() {
    return localStorage.getItem('uzai-refresh-token');
  }

  setRefreshToken(token: string) {
    return localStorage.setItem('uzai-refresh-token', token);
  }

  isLoggedIn() {
    try {
      const refreshToken = this.getRefreshToken();
      if (!this.user || !refreshToken) {
        return false;
      }
      const decoded = jwt_decode<TokenDecoded>(refreshToken);
      const expiration = moment.unix(decoded.exp);
      return expiration.isAfter(moment());
    } catch (err) {
      return false;
    }

  }

  login(email: string, password: string) {
    return this.http
      .post<{ user: User, accessToken: string, refreshToken: string }>(
        `${environment.backend}/auth/login`,
        {
          email,
          password
        }
      ).pipe(
        tap((res) => this.storeSession(res)),
        map((res) => {
          return res.user;
        }),
        catchError((error) => {
          this.removeTokens();
          return throwError(error.error);
        })
      );
  }

  private storeSession(session: Session) {
    this.userSubject.next(session.user);
    localStorage.setItem('uzai-user', JSON.stringify(session.user));
    localStorage.setItem('uzai-access-token', session.accessToken);
    localStorage.setItem('uzai-refresh-token', session.refreshToken);
  }

  /**
   * Replaces the session by a new one issued for another organisation or role (organisation access): stored
   * like a login, without the strong access token of the previous one. The caller reloads the page.
   */
  replaceSession(session: Session) {
    localStorage.removeItem('uzai-strong-access-token');
    this.storeSession(session);
  }

  /** What the login page shows for this e-mail. A POST: the e-mail never sits in a URL. */
  discover(email: string) {
    return firstValueFrom(this.http.post<SsoDiscovery>(`${environment.backend}/auth/sso/discover`, { email }));
  }

  /** Where to send the browser to sign in through the directory. */
  ssoStartUrl(domain: string, returnTo?: string) {
    const params = new URLSearchParams({ domain });
    if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
      params.set('returnTo', returnTo);
    }
    return `${environment.backend}/auth/sso/start?${params.toString()}`;
  }

  /** Turns the one-time code of the directory sign-in into a session, like a password login. */
  exchange(code: string) {
    return this.http
      .post<Session & { returnTo: string | null }>(`${environment.backend}/auth/sso/exchange`, { code })
      .pipe(
        tap((res) => this.storeSession(res)),
        map((res) => ({ user: res.user, returnTo: res.returnTo })),
        catchError((error) => {
          this.removeTokens();
          return throwError(() => error.error);
        })
      );
  }

  sendResetPassword(email: string) {
    return firstValueFrom(this.http
      .post<{}>(
        `${environment.backend}/auth/password/forget/send`,
        {
          email
        }
      ));
  }

  resetPassword(password: string, code: string) {
    return firstValueFrom(this.http
      .post<{}>(
        `${environment.backend}/auth/password/forget/reset`,
        {
          code,
          password
        }
      ));
  }

  refreshToken() {

    return this.http
      .post<{ accessToken: string }>(
        `${environment.backend}/auth/refreshToken`,
        {
          refreshToken: this.getRefreshToken()
        }
      ).pipe(
        tap((res) => {
          localStorage.setItem('uzai-access-token', res.accessToken);
        }),
        map((res) => {
          return res.accessToken;
        }),
        catchError((error) => {
          // An organisation access that is over is not a logout: the interceptor brings the session back to
          // the platform organisation.
          if (error?.error?.message !== 'ACCESS_ENDED') {
            this.logout()
          }
          return throwError(() => error.error);
        })
      );
  }

  getStrongAccessToken(): string | undefined {
    const token = localStorage.getItem('uzai-strong-access-token');
    if (!token) {
      return undefined;
    }
    try {
      const decoded: any = jwt_decode(token);
      if (decoded && moment().isBefore(moment.unix(decoded.exp))) {
        return token;
      }
      localStorage.removeItem('uzai-strong-access-token')
      return undefined;
    } catch (err) {
      localStorage.removeItem('uzai-strong-access-token')
      return undefined;
    }
  }

  requestOtp() {
    return this.http
      .post<{ accessToken: string }>(
        `${environment.backend}/auth/otp`,
        {}
      );
  }

  async validateOtp(otp: string) {
    const res = await firstValueFrom(this.http
      .post<{ strongAccessToken: string }>(
        `${environment.backend}/auth/otp/validate`,
        {
          otp
        }
      ));
    localStorage.setItem('uzai-strong-access-token', res.strongAccessToken);
    return res.strongAccessToken
  }

  setUser(user: User) {
    localStorage.setItem('uzai-user', JSON.stringify(user));
    this.userSubject.next(user);
  }

  removeTokens() {
    this.userSubject.next(undefined);
    localStorage.removeItem('uzai-user');
    localStorage.removeItem('uzai-access-token');
    localStorage.removeItem('uzai-refresh-token');
    localStorage.removeItem('uzai-strong-access-token');
  }

  logout() {
    this._message.emitChange("LOADING", "END");
    // The local session goes whatever the backend answers (inside an organisation access already over, it
    // answers 401 ACCESS_ENDED), then a full reload: several services keep the state of the session that ended.
    const endLocalSession = () => {
      this.removeTokens();
      window.location.assign('/login');
    };
    this.http.delete<void>(
      `${environment.backend}/auth/logout`).subscribe({ next: endLocalSession, error: endLocalSession });
  }

  /** Checks the invitation link and tells how this invitee signs in. */
  retrieveInvitation(nonce: string) {
    return firstValueFrom(this.http.get<SsoDiscovery | null>(`${environment.backend}/auth/invitation/${nonce}`));
  }

  resendInvitation(email: string) {
    return firstValueFrom(this.http.post<void>(`${environment.backend}/auth/invitation/resend`, { email }));
  }

  confirmInvitation(nonce: string, password: string) {
    return this.http
      .post<{ user: User, accessToken: string, refreshToken: string }>(
        `${environment.backend}/auth/invitation/${nonce}`,
        {
          password
        }
      ).pipe(
        tap((res) => this.storeSession(res)),
        map((res) => {
          return res.user;
        }),
        catchError((error) => {
          this.removeTokens();
          return throwError(error.error);
        })
      );
  }
}
